import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';
import { ParagraphActions } from './paragraph.actions';
import { ApplicationFacade } from '@lyceo/shared/data-access';
import { ScopeActions } from '../scope/scope.actions';
import { ParagraphService } from '../../paragraph.service';
import { ParagraphFacade } from './paragraph.facade';
import { isDefined } from '@lyceo/shared/util';
import { routerNavigatedAction } from '@ngrx/router-store';
import { PersonUsesBookActions } from '../person-uses-book/person-uses-book.actions';
import { getParagraphIdsFromScopesDto } from '../scope/scope.functions';
import { PersonUsesBookChildDto } from '../person-uses-book/person-uses-book.model';

@Injectable()
export class ParagraphEffects {
  private readonly actions$ = inject(Actions);
  private readonly paragraphService = inject(ParagraphService);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);
  private readonly paragraphFacade = inject(ParagraphFacade);

  public getParagraphs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        ScopeActions.getPersonScopesSuccess,
        ScopeActions.switchBooksSuccess,
      ),
      map((action) =>
        getParagraphIdsFromScopesDto(action.scopes).filter(
          (id) => !this.paragraphFacade.fullyLoadedIds().includes(id),
        ),
      ),
      filter((ids) => ids.length !== 0),
      exhaustMap((paragraphIds) =>
        this.paragraphService.getParagraphByIds(paragraphIds).pipe(
          map((paragraphs) =>
            ParagraphActions.getParagraphByIdsSuccess({
              paragraphs: paragraphs,
            }),
          ),
          catchError((error: Error) =>
            of(ParagraphActions.getParagraphByIdsFailure({ error })),
          ),
        ),
      ),
    );
  });

  public getParagraphsFromLatestPersonUsesBooks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PersonUsesBookActions.getLatestPersonUsesBooksSuccess),
      map((action) =>
        action.personUsesBooks
          .map((personUsesBook) => personUsesBook.lastUsedParagraphId)
          .filter((id) => !this.paragraphFacade.fullyLoadedIds().includes(id)),
      ),
      filter((ids) => ids.length !== 0),
      exhaustMap((paragraphIds) =>
        this.paragraphService.getParagraphByIds(paragraphIds).pipe(
          map((paragraphs) =>
            ParagraphActions.getParagraphByIdsSuccess({
              paragraphs: paragraphs,
            }),
          ),
          catchError((error: Error) =>
            of(ParagraphActions.getParagraphByIdsFailure({ error })),
          ),
        ),
      ),
    );
  });

  public getParagraphsFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ParagraphActions.getParagraphByIdsFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: 'Het laden van de paragrafen is niet gelukt.',
          });
        }),
      );
    },
    { dispatch: false },
  );

  public getParagraphAfterBookSelect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ScopeActions.switchBookSuccess),
      filter((action) => isDefined(action.scope.personUsesBook)),
      exhaustMap((action) =>
        this.paragraphService
          .getParagraph(
            (action.scope.personUsesBook as PersonUsesBookChildDto)
              .lastUsedParagraphId,
          )
          .pipe(
            map((paragraph) =>
              ParagraphActions.getParagraphByIdSuccess({
                paragraph: paragraph,
              }),
            ),
            catchError((error: Error) =>
              of(ParagraphActions.getParagraphByIdFailure({ error })),
            ),
          ),
      ),
    );
  });

  public paragraphLoaded$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigatedAction),
      filter((action) =>
        action.payload.event.url.startsWith('/practise/book/'),
      ),
      filter(() => !this.paragraphFacade.paramParagraphLoaded()),
      map(() => ParagraphActions.getParagraphById()),
    );
  });

  public getParagraph$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ParagraphActions.getParagraphById),
      map(() => this.paragraphFacade.paramParagraphId()),
      filter(isDefined),
      exhaustMap((paragraphId) =>
        this.paragraphService.getParagraph(paragraphId).pipe(
          map((paragraph) =>
            ParagraphActions.getParagraphByIdSuccess({
              paragraph: paragraph,
            }),
          ),
          catchError((error: Error) =>
            of(ParagraphActions.getParagraphByIdFailure({ error })),
          ),
        ),
      ),
    );
  });

  public getParagraphFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ParagraphActions.getParagraphByIdFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: 'Het laden van de paragraaf is niet gelukt.',
          });
        }),
      );
    },
    { dispatch: false },
  );
}
